import type { HomeEconomy } from "@/home/economy";
import { getGameSettings, type GameSettings } from "@/game/model/game-settings";
import type { CombatRatings } from "@/game/model/character-stats";
import type { HomeStructureCardCounts } from "@/home/home-structure-types";
import { normalizePartKind } from "@/home/part-kind";
import type { HomeRoomData } from "@/home/storage";

export type HomeStructureStatConfig = Pick<
  GameSettings,
  | "homeStructureBaseHp"
  | "homeStructureBaseDef"
  | "homeStructureBaseRegenPerSec"
  | "homeStructureHpPerCard"
  | "homeStructureDefPerCard"
  | "homeStructureRegenPerCard"
  | "homeStructureHpCardMax"
  | "homeStructureDefCardMax"
  | "homeStructureRegenCardMax"
  | "homeStructureShieldHours"
  | "homeStructureRegenDelayAfterHitSec"
  | "homeStructurePotionHeal"
>;

export function homeStructureStatConfigFromSettings(
  settings?: GameSettings,
): HomeStructureStatConfig {
  const s = settings ?? getGameSettings();
  return {
    homeStructureBaseHp: s.homeStructureBaseHp,
    homeStructureBaseDef: s.homeStructureBaseDef,
    homeStructureBaseRegenPerSec: s.homeStructureBaseRegenPerSec,
    homeStructureHpPerCard: s.homeStructureHpPerCard,
    homeStructureDefPerCard: s.homeStructureDefPerCard,
    homeStructureRegenPerCard: s.homeStructureRegenPerCard,
    homeStructureHpCardMax: s.homeStructureHpCardMax,
    homeStructureDefCardMax: s.homeStructureDefCardMax,
    homeStructureRegenCardMax: s.homeStructureRegenCardMax,
    homeStructureShieldHours: s.homeStructureShieldHours,
    homeStructureRegenDelayAfterHitSec: s.homeStructureRegenDelayAfterHitSec,
    homeStructurePotionHeal: s.homeStructurePotionHeal,
  };
}

export function homeStructureCardsFromEconomy(economy: HomeEconomy): HomeStructureCardCounts {
  return {
    hpCards: Math.max(0, Math.floor(economy.homeStructureHpCardsApplied ?? 0)),
    defCards: Math.max(0, Math.floor(economy.homeStructureDefCardsApplied ?? 0)),
    regenCards: Math.max(0, Math.floor(economy.homeStructureRegenCardsApplied ?? 0)),
  };
}

export function clampStructureCardApplications(
  cards: HomeStructureCardCounts,
  config: HomeStructureStatConfig,
): HomeStructureCardCounts {
  return {
    hpCards: Math.min(config.homeStructureHpCardMax, Math.max(0, cards.hpCards)),
    defCards: Math.min(config.homeStructureDefCardMax, Math.max(0, cards.defCards)),
    regenCards: Math.min(config.homeStructureRegenCardMax, Math.max(0, cards.regenCards)),
  };
}

export function homeStructureMaxHp(
  cards: HomeStructureCardCounts,
  config: HomeStructureStatConfig,
): number {
  const c = clampStructureCardApplications(cards, config);
  return Math.max(1, config.homeStructureBaseHp + c.hpCards * config.homeStructureHpPerCard);
}

export function homeStructureDefense(
  cards: HomeStructureCardCounts,
  config: HomeStructureStatConfig,
): number {
  const c = clampStructureCardApplications(cards, config);
  return Math.max(0, config.homeStructureBaseDef + c.defCards * config.homeStructureDefPerCard);
}

export function homeStructureRegenPerSec(
  cards: HomeStructureCardCounts,
  config: HomeStructureStatConfig,
): number {
  const c = clampStructureCardApplications(cards, config);
  return Math.max(
    0,
    config.homeStructureBaseRegenPerSec + c.regenCards * config.homeStructureRegenPerCard,
  );
}

export function homeStructureDefenderRatings(
  cards: HomeStructureCardCounts,
  config?: HomeStructureStatConfig,
): CombatRatings {
  const cfg = config ?? homeStructureStatConfigFromSettings();
  const defense = homeStructureDefense(cards, cfg);
  return {
    level: 1,
    attack: 0,
    defense,
    dodge: 0,
    hit: 0,
    critChance: 0,
    critMultiplier: 1,
    attackSpeed: 0,
    moveSpeed: 0,
    hpRegenPerSec: 0,
    manaMaxBonus: 0,
  };
}

export function findSolidPlacementId(homeData: HomeRoomData): string | null {
  for (const placement of homeData.placements) {
    const part = homeData.parts.find((p) => p.id === placement.partId);
    if (!part) continue;
    if (normalizePartKind(part.kind) === "solid") return placement.id;
  }
  return null;
}
